/**
 * Chart
 * Classification: third-party-wrapper
 *
 * Tier 3: the config-driven entry point. A `ChartConfig` is serialisable, so a
 * user-built dashboard can persist one and render it here.
 *
 * `kpi` and `table` are deliberately part of the same union: a dashboard
 * builder keeps one config -> one component with no call-site special-casing,
 * even though neither renders as an ECharts instance.
 */
import { useMemo } from 'react';
import type { EChartsOption } from 'echarts';
import { useTheme } from '@mui/material/styles';
import { Skeleton } from '@/atoms/Skeleton';
import { EmptyState } from '@/organisms/EmptyState';
import { StatTile } from '@/molecules/StatTile';
import type { ChartMedia, ChartMode } from '@/tokens/chart';
import type { ChartConfig, ChartPoint, ChartRow, ChartState } from './types';
import { useChartOption } from './useChartOption';
import { ChartSurface } from './ChartSurface';

export interface ChartBaseProps {
  data: readonly ChartRow[];
  /** Defaults to the surrounding MUI theme's palette mode. */
  mode?: ChartMode;
  /** `export` renders SVG, static and non-interactive, for PDF capture. */
  media?: ChartMedia;
  height?: number | string;
  /**
   * Only conditions the component cannot observe. Emptiness is derived from
   * `data`, so it is deliberately not expressible here.
   */
  state?: ChartState;
  onPointClick?: (point: ChartPoint) => void;
  /** Last-resort hook for options the config schema does not model. */
  transformOption?: (option: EChartsOption) => EChartsOption;
  /** Accessible name. Falls back to the config title. */
  label?: string;
  /**
   * Overlays decal textures on every mark as a second identity channel beyond
   * hue. Off by default — see `ChartConfig.decals`.
   *
   * Exists as a prop as well as a config field because the tier-2 adapters
   * (`BarChart` and friends) build their own config and never expose one. When
   * both are given, this wins, so a caller can flip it per render without
   * rewriting a persisted config.
   */
  decals?: boolean;
}

export interface ChartProps extends ChartBaseProps {
  config: ChartConfig;
}

export function Chart({
  config,
  data,
  mode,
  media = 'screen',
  height = 320,
  state,
  onPointClick,
  transformOption,
  label,
  decals,
}: ChartProps) {
  const theme = useTheme();
  const resolvedMode: ChartMode = mode ?? (theme.palette.mode === 'light' ? 'light' : 'dark');
  // Memoized, and passed through untouched when the prop is absent: spreading
  // a fresh object every render would defeat `useChartOption`'s memo and push a
  // new option into `ChartSurface.setOption` on every render.
  const resolvedConfig = useMemo(
    () => (decals === undefined ? config : { ...config, decals }),
    [config, decals],
  );
  const built = useChartOption(resolvedConfig, data, resolvedMode, media);
  const option = transformOption ? transformOption(built) : built;
  const name = label ?? config.title ?? 'Chart';

  if (state === 'error') {
    return <EmptyState title="Chart unavailable" description="This data could not be loaded." />;
  }
  if (state === 'loading') {
    return <Skeleton variant="rectangular" height={height} aria-label={`${name} loading`} />;
  }
  // Emptiness is derived, never passed — a caller cannot contradict the data.
  if (data.length === 0) {
    return (
      <EmptyState title="No data" description="There is nothing to show for this selection." />
    );
  }

  if (config.chartType === 'kpi') {
    const entry = config.series.find((s) => !s.hidden);
    const field = entry?.fields.y ?? entry?.fields.value;
    const last = field ? data[data.length - 1]?.[field] : undefined;
    return (
      <StatTile
        value={String(last ?? '—')}
        label={config.title ?? entry?.name ?? 'Value'}
        caption={config.subtitle}
      />
    );
  }

  if (config.chartType === 'table') {
    // Rendering a result set as a table is the Table organism's job; doing it
    // here would duplicate sorting, virtualisation and column sizing.
    return (
      <EmptyState
        title="Table view"
        description="Render this result set with the Table organism from @aistrike-dev/ui."
      />
    );
  }

  return (
    <ChartSurface
      option={option}
      mode={resolvedMode}
      media={media}
      height={height}
      onPointClick={onPointClick}
      label={name}
    />
  );
}
